import React, { useRef, useState } from "react";
import { Card } from "primereact/card";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import { useShareHolderDetails } from "./ShareHolderDetail";
import "./shareholder-form.scss";
import PermissionGate from "~/components/PermissionGate";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import API_ROUTES_V2 from "~/api-routes-v2";
import apiService from "~/services/service";
import { useSelector } from "react-redux";
import { UserRole } from "~/types/roles";

const ShareHolderSideBar = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const location = useLocation();
  const { entityId, shareholderId } = useParams<{
    entityId: string;
    shareholderId: string;
  }>();
  // URL pattern: /entity/:entityId/share-holders/:shareholderId/:tab
  const activeTab = location.pathname.split("/")[5] || "info";
  const { regData, loading } = useShareHolderDetails();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const navigate = useNavigate();
  const toastRef = useRef<Toast>(null);
  const { role } = useSelector((state) => state.login);

  const getInitials = (fullName) => {
    const nameArray = fullName.split(" ");
    const firstInitial = nameArray[0] ? nameArray[0].charAt(0) : "";
    const lastInitial =
      nameArray.length > 1 ? nameArray[nameArray.length - 1].charAt(0) : "";
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  const hasEntityPlusAccess = regData?.entityData?.active_services?.some(
    (e) => e.name === "Entities +",
  );

  const deleteShareholder = async (id: string) => {
    const response = await apiService(
      API_ROUTES_V2.DELETE_SHAREHOLDER,
      "DELETE",
      {
        replace: { id, entity_id: entityId },
      },
    );
    return response;
  };

  const handleDeleteConfirm = async () => {
    if (!shareholderId) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Shareholder ID is missing",
      });
      return;
    }

    const entityId = regData?.entities?.[0];
    if (!entityId) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Entity ID is missing",
      });
      return;
    }

    try {
      setIsDeleting(true);
      await deleteShareholder(shareholderId);
      setShowDeleteDialog(false);
      navigate(`/entity/${entityId}/appointments-ownership?tab=shareholders`, {
        state: {
          showDeleteToast: true,
          shareholderId: shareholderId,
          shareholderName:
            regData?.type === "Individual"
              ? `${regData?.name} ${regData?.last_name}`
              : regData?.legal_name,
        },
      });
    } catch (error) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while deleting profile",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Card className="shareholder-details-card">
        <div className="flex flex-column shareholder-profile">
          <div className="avatar-container mb-3">
            <h3>
              {getInitials(
                regData?.type === "Individual"
                  ? `${regData?.name} ${regData?.last_name}`
                  : `${regData?.legal_name}`,
              )}
            </h3>
          </div>
          {!loading ? (
            <p className="shareholder__name m-0">
              {regData?.type === "Individual"
                ? `${regData?.name} ${regData?.last_name}`
                : `${regData?.legal_name}`}
            </p>
          ) : (
            <Skeleton width="150px" height="20px" className="mr-1" />
          )}
          <p className="m-0">
            {!loading ? (
              <p className="m-0">
                {regData?.type === "Individual"
                  ? "Individual Shareholder"
                  : "Corporate Shareholder"}
              </p>
            ) : (
              <Skeleton width="150px" height="20px" className="mr-1" />
            )}
          </p>
        </div>
        {![UserRole.LSP_MANAGER, UserRole.LSP_USER].includes(role) &&
          (regData?.email || (regData?.phone_code && regData?.phone)) && (
            <div className="other_datail-container">
              {regData?.email && (
                <span className="flex align-items-center  gap-2 other__detail mb-2">
                  <i className="pi pi-envelope" />
                  {!loading ? (
                    regData?.email
                  ) : (
                    <Skeleton width="150px" height="20px" className="mr-1" />
                  )}
                </span>
              )}
              {regData?.phone_code && regData?.phone && (
                <span className="flex align-items-center  gap-2 other__detail">
                  <i className="pi pi-phone" />{" "}
                  {!loading ? (
                    regData?.phone_code + " " + regData?.phone
                  ) : (
                    <Skeleton width="150px" height="20px" className="mr-1" />
                  )}
                </span>
              )}
            </div>
          )}
        <PermissionGate
          resource="shareholder"
          action="modify"
          resourceAttr={{ entity_id: entityId }}
        >
          <div className="flex justify-content-center">
            <Button
              className="edit-button"
              onClick={() => setShowDeleteDialog(true)}
            >
              Delete profile <span className="pi pi-trash mb-1 ml-2" />
            </Button>
          </div>
        </PermissionGate>
        <div className="flex flex-column gap-1">
          <Link
            to={`/entity/${entityId}/share-holders/${shareholderId}/info`}
            className={`sidebar-align ${activeTab === "info" ? "active-tab" : "menu-hover"}`}
          >
            <p className="sidebar-menu">Shareholder Information</p>
            <span className="pi pi-angle-right icon-color"></span>
          </Link>
          {hasEntityPlusAccess && (
            <Link
              to={`/entity/${entityId}/share-holders/${shareholderId}/address`}
              className={`sidebar-align ${activeTab === "address" ? "active-tab" : "menu-hover"}`}
            >
              <p className="sidebar-menu">Address</p>
              <span className="pi pi-angle-right icon-color"></span>
            </Link>
          )}
          <Link
            to={`/entity/${entityId}/share-holders/${shareholderId}/registry`}
            className={`sidebar-align ${activeTab === "registry" ? "active-tab" : "menu-hover"}`}
          >
            <p className="sidebar-menu">Share Registry</p>
            <span className="pi pi-angle-right icon-color"></span>
          </Link>
          {hasEntityPlusAccess && (
            <Link
              to={`/entity/${entityId}/share-holders/${shareholderId}/attachments`}
              className={`sidebar-align ${activeTab === "attachments" ? "active-tab" : "menu-hover"}`}
            >
              <p className="sidebar-menu">Attachments</p>
              <span className="pi pi-angle-right icon-color"></span>
            </Link>
          )}
        </div>
      </Card>
      <DeleteConfirmationDialog
        visible={showDeleteDialog}
        loading={isDeleting}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
      />
      <Toast ref={toastRef} position="bottom-right" />
    </>
  );
};

export default ShareHolderSideBar;
